import React from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import CollapsibleSection from '../../../components/form/CollapsibleSection';
import { ScheduleStatusBadge } from './StatusBadge';
import {
  buildScheduledMessagesEditPath,
  buildUserContentPlaceholder,
  buildUserStatusHint,
  contactLabel,
  formatDateTime,
  formatScheduleType,
} from '../utils';
import { buildScheduledMessageLoadingState } from '../scheduledMessageLoadingState';
import ScheduleSummaryDisplay from './ScheduleSummaryDisplay';
import ScheduledMessageDetailActions from './ScheduledMessageDetailActions';

function CompactField({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-medium text-content-muted">{label}</dt>
      <dd className="text-sm text-content-primary mt-1 break-all">{children}</dd>
    </div>
  );
}

function ActionErrors({ cancelError, deleteError, retryError, resendError, dispatchError, sendTemplateError }) {
  return (
    <>
      {cancelError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{cancelError}</p>
        </div>
      )}
      {deleteError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{deleteError}</p>
        </div>
      )}
      {retryError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{retryError}</p>
        </div>
      )}
      {resendError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{resendError}</p>
        </div>
      )}
      {dispatchError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{dispatchError}</p>
        </div>
      )}
      {sendTemplateError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{sendTemplateError}</p>
        </div>
      )}
    </>
  );
}

function ScheduledMessageUserDetail({
  item,
  mode,
  agentId,
  listPath,
  downloadableAttachments,
  cancelError,
  deleteError,
  retryError,
  resendError,
  dispatchError,
  sendTemplateError,
  cancelling,
  deleting,
  retrying,
  resending,
  dispatching,
  sendingTemplate,
  onCancel,
  onDelete,
  onRetry,
  onResend,
  onForceDispatch,
  onSendTemplate,
  onBack,
}) {
  const statusHint = buildUserStatusHint(item);
  const hasContent = Boolean(item.generated_content);
  const hasAttachments = downloadableAttachments.length > 0;
  const loadingState = buildScheduledMessageLoadingState({
    itemId: item.id,
    cancelling,
    deleting,
    retrying,
    resending,
    dispatching,
    sendingTemplate,
  });
  const editTo = buildScheduledMessagesEditPath(mode, {
    agentId: agentId || item.agent_id,
    scheduledMessageId: item.id,
  });

  return (
    <div className="space-y-6">
      <div>
        <Link to={listPath} className="text-sm text-forge-700 hover:text-forge-800 font-medium">
          ← Voltar aos agendamentos
        </Link>
        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-content-primary">Detalhe do agendamento</h1>
            <p className="text-content-muted text-sm mt-1">
              {contactLabel(item)} · {item.template_name}
            </p>
          </div>
          <ScheduleStatusBadge status={item.status} />
        </div>
      </div>

      <section className="surface-card p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-content-muted">
          Próximo envio
        </p>
        <p className="mt-1 text-xl font-semibold text-content-primary">
          {formatDateTime(item.next_run_at)}
        </p>
        {statusHint && (
          <p className="mt-2 text-sm text-content-secondary">{statusHint}</p>
        )}
      </section>

      <ActionErrors
        cancelError={cancelError}
        deleteError={deleteError}
        retryError={retryError}
        resendError={resendError}
        dispatchError={dispatchError}
        sendTemplateError={sendTemplateError}
      />

      <section className="surface-card p-4 space-y-4">
        <h2 className="text-sm font-semibold text-content-primary">Mensagem</h2>

        {hasContent ? (
          <div>
            <p className="text-xs text-content-muted mb-3">
              Gerado em {formatDateTime(item.generated_at)}
            </p>
            <div className="rounded-lg border border-surface-border bg-slate-50 p-3 text-sm text-content-primary whitespace-pre-wrap">
              {item.generated_content}
            </div>
          </div>
        ) : (
          <p className="text-sm text-content-muted">{buildUserContentPlaceholder(item)}</p>
        )}

        {hasAttachments && (
          <div>
            <p className="text-xs font-medium text-content-muted mb-2">Arquivos gerados</p>
            <div className="flex flex-wrap gap-2">
              {downloadableAttachments.map((attachment, index) => {
                const label = attachment.file_name
                  ? `Baixar ${attachment.file_name}`
                  : 'Baixar arquivo';
                return (
                  <a
                    key={`${attachment.download_url}-${index}`}
                    href={attachment.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={attachment.file_name || undefined}
                    className="inline-flex items-center gap-2 btn-secondary text-sm px-4 py-2"
                  >
                    <Download className="h-4 w-4 shrink-0" aria-hidden />
                    {label}
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <section className="surface-card p-4">
        <h2 className="text-sm font-semibold text-content-primary mb-4">Agendamento</h2>
        <dl className="space-y-4">
          <CompactField label="Contato">{contactLabel(item)}</CompactField>
          <CompactField label="Template WhatsApp">{item.template_name}</CompactField>
          <CompactField label="Quando envia">
            <ScheduleSummaryDisplay item={item} />
            <p className="text-xs text-content-muted mt-1">{formatScheduleType(item.schedule_type)}</p>
          </CompactField>
          {item.ends_at && (
            <CompactField label="Término">{formatDateTime(item.ends_at)}</CompactField>
          )}
          {item.last_sent_at && (
            <CompactField label="Último envio">{formatDateTime(item.last_sent_at)}</CompactField>
          )}
          {item.schedule_type === 'recurring' && (
            <CompactField label="Envios realizados">{item.sent_count ?? 0}</CompactField>
          )}
        </dl>
      </section>

      {item.last_error && (
        <section className="rounded-xl border border-red-200 bg-red-50 p-4">
          <h2 className="text-sm font-semibold text-red-800 mb-2">Último erro</h2>
          <p className="text-sm text-red-700 whitespace-pre-wrap">{item.last_error}</p>
        </section>
      )}

      {item.generation_instruction && (
        <CollapsibleSection
          title="Instruções para a IA"
          summary="Ver instrução usada para gerar a mensagem"
          defaultOpen={false}
        >
          <p className="text-sm text-content-primary whitespace-pre-wrap">
            {item.generation_instruction}
          </p>
        </CollapsibleSection>
      )}

      <ScheduledMessageDetailActions
        item={item}
        editTo={editTo}
        loadingState={loadingState}
        onCancel={onCancel}
        onDelete={onDelete}
        onRetry={onRetry}
        onResend={onResend}
        onForceDispatch={onForceDispatch}
        onSendTemplate={onSendTemplate}
        onBack={onBack}
      />
    </div>
  );
}

export default ScheduledMessageUserDetail;
